import { ArrowRightOutlined, DownOutlined, UpOutlined } from "@ant-design/icons";
import {
  Button,
  Col,
  Flex,
  Row,
  Segmented,
  Space,
  theme,
  Typography,
} from "antd";
import { BanknoteCheck } from "lucide-react";
import { ReactNode, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMediaQuery } from "usehooks-ts";
import Card from "src/components/Card";
import Loading from "src/components/loading";
import IrtWithdrawForm from "src/components/wallet/IrtWithdrawForm";
import UsdtWithdrawForm from "src/components/wallet/UsdtWithdrawForm";
import useOrg from "src/hooks/apis/useOrg";
import useIsDesktop from "src/hooks/useIsDesktop";
import useUser from "src/hooks/useUser";
import { IS_WHITE_LABEL } from "src/constants";

type WithdrawMode = "IRT" | "USDT";

/**
 * Guide card. On desktop the content is always shown; on mobile it is clamped
 * with a fade-out at the bottom and a "show more/less" toggle so the long guide
 * doesn't push the form off-screen.
 */
function GuideCard({ title, children }: { title: string; children: ReactNode }) {
  const isDesktop = useIsDesktop();
  const [expanded, setExpanded] = useState(false);
  const { token } = theme.useToken();
  const collapsed = !isDesktop && !expanded;

  return (
    <Card noHeader>
      <Typography.Title level={5} className="mt-0 mb-3">
        {title}
      </Typography.Title>
      <div
        className="relative overflow-hidden transition-[max-height] duration-300"
        style={{ maxHeight: collapsed ? 140 : 3000 }}
      >
        {children}
        {collapsed && (
          <div
            className="absolute inset-x-0 bottom-0 h-16 pointer-events-none"
            style={{ background: `linear-gradient(to top, ${token.colorBgContainer}, transparent)` }}
          />
        )}
      </div>
      {!isDesktop && (
        <Button
          type="link"
          size="small"
          className="p-0 mt-1"
          icon={expanded ? <UpOutlined /> : <DownOutlined />}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? "نمایش کمتر" : "نمایش بیشتر"}
        </Button>
      )}
    </Card>
  );
}

function Withdraw() {
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const { isLoading: userLoading } = useUser();
  const { data: org, isLoading: orgLoading } = useOrg();
  const isWhiteLabel = org?.config?.whiteLabel ?? IS_WHITE_LABEL;

  const [mode, setMode] = useState<WithdrawMode>("IRT");

  if (userLoading || orgLoading) {
    return <Loading size="fill" text="در حال بارگذاری..." />;
  }

  return (
    <div className="max-w-5xl mx-auto">
      <Flex orientation="vertical" justify="space-between" align="flex-start" className="mb-4" wrap="wrap" gap="12px">

        {/* On mobile the fixed top navbar provides the back button */}
        {!isMobile && (
          <Button
            type="text"
            icon={<ArrowRightOutlined />}
            iconPlacement="start"
            onClick={() => navigate("/finance")}
            className="text-gray-600"
          >
            مالی
          </Button>
        )}
        {!isWhiteLabel && (
          <Segmented
            value={mode}
            onChange={(v) => setMode(v as WithdrawMode)}
            options={[
              { label: "برداشت تومان", value: "IRT", },
              { label: "برداشت تتر", value: "USDT" },
            ]}
            size="large"
            className="[&_.ant-segmented-item+.ant-segmented-item]:ms-2"
          />
        )}
      </Flex>



      <Row gutter={[24, 24]} align="stretch">


        {/* Right column: form */}
        <Col xs={24} lg={isWhiteLabel ? 24 : 14}>
          <Card title="موجودی" Icon={BanknoteCheck}>
            {mode === "IRT" && <IrtWithdrawForm />}
            {!isWhiteLabel && mode === "USDT" && <UsdtWithdrawForm />}
          </Card>
        </Col>


        {/* Left column: info, guide, FAQ */}
        {!isWhiteLabel && <Col xs={24} lg={10}>
          <Space orientation="vertical" size="middle" style={{ width: "100%" }}>

            {!isWhiteLabel && mode === "USDT" && (
              <GuideCard title="راهنمای برداشت USDT">
                <Typography.Paragraph className="text-base">
                  ۱. شبکه انتقال (BEP20 یا TRC20) را انتخاب کنید.
                  <br />
                  ۲. آدرس کیف پول مقصد خود را وارد کنید. آدرس BEP20 با ۰x شروع می‌شود و آدرس TRC20 با T شروع می‌شود.
                  <br />
                  ۳. مقدار برداشت را وارد کنید (حداقل ۱۰۰ USDT). با دکمه «حد اکثر مبلغ» می‌توانید حداکثر مبلغ قابل برداشت را وارد کنید.
                  <br />
                  ۴. پس از تأیید کارمزد و هزینه انتقال، روی «درخواست برداشت» کلیک کرده و کد تایید را وارد کنید.
                </Typography.Paragraph>
                <Typography.Paragraph className="text-base">
                  <strong>توجه:</strong> شبکه انتخابی باید با شبکه کیف پول مقصد شما یکی باشد. واریز به شبکه اشتباه موجب از دست رفتن دارایی می‌شود.
                </Typography.Paragraph>
              </GuideCard>
            )}
            {mode === "IRT" && (
              <GuideCard title="راهنمای برداشت تومان">
                {/* <Alert
                  type="warning"
                  icon={<WarningOutlined />}
                  className="mb-4"
                  title="با توجه به شرایط ناپایدار ناشی از جنگ و نوسانات نرخ پوند، این امکان وجود دارد که نرخ‌ها با بازار آزاد اختلاف داشته باشند و همچنین پرداخت‌ها با تأخیر انجام شوند. بنابراین توصیه می‌شود در صورت امکان، تا زمان بازگشت ثبات به بازار، از برداشت وجه خودداری فرمایید."
                /> */}
                <Typography.Paragraph className="text-base">
                  برداشت فقط به شماره شباهای تأییدشده در حساب شما واریز می‌شود.
                </Typography.Paragraph>

                <Typography.Paragraph className="text-base">
                  با دکمه «حد اکثر مبلغ» می‌توانید حداکثر مبلغی را که پس از کسر کارمزد قابل برداشت است، یک‌جا وارد کنید. پس از وارد کردن مبلغ، با کلیک روی «درخواست برداشت» در پنجره تأیید، درخواست شما ثبت می‌شود.
                </Typography.Paragraph>
                <Typography.Paragraph className="text-base">
                  درخواست‌های ثبت‌شده در صف سیکل پایا قرار می‌گیرند و در اولین سیکل یا حداکثر تا ۷۲ ساعت تسویه می‌شوند.
                </Typography.Paragraph>
              </GuideCard>
            )}

          </Space>
        </Col>}

      </Row>
    </div >
  );
}

export default Withdraw;
